import React, {useEffect, useState} from "react";

import {GoogleClientId} from "../../../../config";
import GoogleOAuthProvider, {useGoogleOAuth} from "../GoogleOAuth/GoogleOAuthContext";
import useGoogleAuthorizationCode from "../GoogleOAuth/useGoogleAuthorizationCode";

import hasGrantedAllScopesGoogle from "../GoogleOAuth/hasGrantedAllScopes";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import GoogleButton from "../GoogleOAuth/GoogleButton";


const StepGmailConsents = (props) => {
    return <GoogleOAuthProvider clientId={GoogleClientId}>
        <StepGmailConsentsInner {...props}/>
    </GoogleOAuthProvider>
}

const StepGmailConsentsInner = ({setDisableNext, googleAccount, createGoogleAccount, openConsentOnMount}) => {
    const scopes = "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.send";  // list of scopes separated by " "
    const scopesArray = scopes.split(" ");
    const [state, setState] = useState({consentCompleted: false, grantedAllScope: false, error: false});
    const [isSaving, setIsSaving] = useState(false);
    const {scriptLoadedSuccessfully} = useGoogleOAuth();
    const login = useGoogleAuthorizationCode({
        onSuccess: codeResponse => handleSuccess(codeResponse),
        onError: codeResponse => handleError(codeResponse),
        scope: scopes,
    });

    const handleLoginWithGoogle = () => {
        if (scriptLoadedSuccessfully) {
            login();
        }
    }

    const handleError = () => {
        setState({
            consentCompleted: true,
            grantedAllScope: false,
            error: true,
        });
    }

    const handleSuccess = async (data) => {
        try {
            const grantedAll = hasGrantedAllScopesGoogle(data, ...scopesArray)
            setIsSaving(true);
            await createGoogleAccount({...data});
            setState({
                consentCompleted: true,
                grantedAllScope: grantedAll,
                error: false,
            })
            if (grantedAll === true) {
                setDisableNext(false);
            }
        } catch (e) {
            handleError();
        }
        setIsSaving(false);
    }

    useEffect(
        () => {
            if (!!openConsentOnMount && googleAccount === null) {
                const timerId = setTimeout(handleLoginWithGoogle, 1500);
                return () => clearInterval(timerId);
            }
        },
        [scriptLoadedSuccessfully] // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
        () => {
            if(googleAccount === null){
                setDisableNext(true);
                return () => setDisableNext(false);
            }
        },
        [scriptLoadedSuccessfully] // eslint-disable-line react-hooks/exhaustive-deps
    )

    return <Stack spacing={3}>
        {
            isSaving ?
                <Alert severity="info">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size="16px"/>
                        <Typography variant="body2">
                            Creazione account Google in corso...
                        </Typography>
                    </Stack>
                </Alert>
                :
                <>
                    {
                        state.consentCompleted === true ?
                            <AfterConsent googleAccount={googleAccount}
                                          grantedAllScope={state.grantedAllScope}
                                          error={state.error}
                                          handleLoginWithGoogle={handleLoginWithGoogle}
                            />
                            :
                            <BeforeConsent googleAccount={googleAccount}
                                           handleLoginWithGoogle={handleLoginWithGoogle}/>
                    }
                </>
        }
    </Stack>
}

const AfterConsent = ({googleAccount, grantedAllScope, error, handleLoginWithGoogle}) => {
    return <>
        {
            grantedAllScope === true &&
            <Alert severity="success">
                Account Google <strong>{googleAccount?.email}</strong> configurato correttamente.
            </Alert>
        }
        {
            grantedAllScope === false && error === false &&
            <Alert severity="error">
                Non hai selezionato tutte le autorizzazzioni necessarie, l'account non è pronto.
                Ripetere il login con Google.
            </Alert>
        }
        {
            error === true &&
            <Alert severity="error">
                Si è verificato un errore, l'account di Google non è stato configurato correttamente.
                Ripetere il login con Google.
            </Alert>
        }
        {
            (error === true || grantedAllScope === false) &&
            <Box textAlign="center">
                <Button onClick={handleLoginWithGoogle} color="primary" variant="contained" size="small">
                    Login con Google
                </Button>
            </Box>
        }
    </>
}

const BeforeConsent = ({googleAccount, handleLoginWithGoogle}) => {
    return <>
        {
            !!googleAccount ?
                <>
                    <Typography variant="body2">
                        L'accout gmail <strong>{googleAccount.email}</strong> è già stato configurato.
                    </Typography>
                    <Typography variant="body2">
                        Premere "Avanti" per utilizzare l'account esistente, "Aggiorna Account" per
                        aggornare l'account esistente o "Nuovo Account" per configurarne
                        uno nuovo account.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button size="small" onClick={handleLoginWithGoogle} color="secondary"
                                variant="outlined">
                            Aggiorna Account
                        </Button>
                        <Button size="small" onClick={handleLoginWithGoogle} color="secondary"
                                variant="contained">
                            Nuovo account
                        </Button>
                    </Stack>
                </>
                :
                <>
                    <Typography variant="body2">
                        Per completare la configurazione è necessario consentire a LivignoItaly di inviare email tramite Gmail.
                        Potrai revocare il consenso in qualsiasi momento.
                    </Typography>
                    <Typography variant="body2">
                        Clicca sul pulsante "Accedi con Google" e segui le istruzioni.
                    </Typography>
                    <Box textAlign="center">
                        <GoogleButton onClick={handleLoginWithGoogle}>
                            Accedi con Google
                        </GoogleButton>
                    </Box>
                </>
        }
    </>
}

export default StepGmailConsents;